/**
 * Source Detection Utility
 * Auto-detects which recruitment channel brought a candidate to Diil.
 *
 * Priority:
 * 1. UTM params (utm_source)
 * 2. Platform click IDs (gclid, fbclid, igshid, …)
 * 3. HTTP Referer — a known site maps to its channel name, any other outside site is
 *    recorded by its hostname (never folded into "direct"), Diil's own domains are ignored
 * 4. The app the page is open inside (Instagram, Facebook, TikTok… in-app browsers, the
 *    Toki webview) — these usually send no referrer at all
 * 5. Default: "direct" (typed URL, bookmark, QR code, SMS, an app that strips the referrer)
 *
 * Values already in the data keep their spelling: "facebook", "instagram", "google",
 * "telegram", "career_page", and the raw utm_source as given ("fb", "ig", "benzin.mn"…).
 */

import { isTokiWebView } from './tokiBridge';
import {
  sessionGetJson,
  sessionSetJson,
  resetViewSession,
} from './trackingSession';

const SESSION_KEY = 'candidate_source';

// Click id → source. `medium` is only "paid" where the id exists solely on ad clicks:
// Facebook adds fbclid to ordinary (organic) outbound links as well, and igshid is
// Instagram's share-link id, so neither says anything about payment.
const PLATFORM_ID_MAP = [
  ['gclid', 'google_ads', 'paid'],
  ['gbraid', 'google_ads', 'paid'],
  ['wbraid', 'google_ads', 'paid'],
  ['msclkid', 'bing_ads', 'paid'],
  ['yclid', 'yandex_ads', 'paid'],
  ['ttclid', 'tiktok', 'paid'],
  ['li_fat_id', 'linkedin', 'paid'],
  ['twclid', 'twitter', 'paid'],
  ['fbclid', 'facebook', null],
  ['igshid', 'instagram', 'social'],
];

// Referer domain → source. Matched on the domain itself and any subdomain of it.
const REFERER_MAP = {
  'facebook.com': 'facebook',
  'fb.com': 'facebook',
  'messenger.com': 'messenger',
  'instagram.com': 'instagram',
  'threads.net': 'threads',
  'threads.com': 'threads',
  't.me': 'telegram',
  'telegram.org': 'telegram',
  'tiktok.com': 'tiktok',
  'linkedin.com': 'linkedin',
  'lnkd.in': 'linkedin',
  'twitter.com': 'twitter',
  'x.com': 'twitter',
  't.co': 'twitter',
  'youtube.com': 'youtube',
  'youtu.be': 'youtube',
  'bing.com': 'bing',
  'yandex.ru': 'yandex',
  'yandex.com': 'yandex',
  'duckduckgo.com': 'duckduckgo',
  'careerbox.mn': 'career_page',
  'careerbox.metacog.mn': 'career_page',
  // Matches the utm_source ChatGPT itself appends, so both arrive as one channel
  'chatgpt.com': 'chatgpt.com',
  'chat.openai.com': 'chatgpt.com',
  'perplexity.ai': 'perplexity',
};

// Diil's own sites. A referrer from one of these is navigation inside Diil (or a new tab
// opened from it), not a channel.
const OWN_DOMAINS = ['diil.app', 'diil.work', 'ajob.uz', 'localhost'];

// Android apps send `android-app://<package>/` as the referrer.
const ANDROID_APP_MAP = {
  'com.google.android.gm': 'gmail',
  'com.google.android.googlequicksearchbox': 'google',
  'com.google.android.youtube': 'youtube',
  'org.telegram.messenger': 'telegram',
  'com.facebook.katana': 'facebook',
  'com.facebook.orca': 'messenger',
  'com.instagram.android': 'instagram',
};

const matchesDomain = (host, domain) => host === domain || host.endsWith(`.${domain}`);

export const isOwnHost = (host) =>
  !!host &&
  (OWN_DOMAINS.some((domain) => matchesDomain(host, domain)) ||
    host.endsWith('.vercel.app') ||
    host === '127.0.0.1');

/**
 * Map a referrer URL to a channel name.
 * @returns {string|null} channel, or null for no referrer / Diil's own pages
 */
export function sourceFromReferrer(referrer) {
  if (!referrer) return null;
  let url;
  try {
    url = new URL(referrer);
  } catch (error) {
    return null;
  }
  if (url.protocol === 'android-app:') {
    const pkg = url.hostname.toLowerCase();
    return ANDROID_APP_MAP[pkg] || pkg || null;
  }
  const host = url.hostname.toLowerCase().replace(/^www\./, '');
  if (!host || isOwnHost(host)) return null;
  // google.com, google.mn, google.co.uk, news.google.com …
  if (/(^|\.)google\.[a-z.]+$/.test(host)) return 'google';
  for (const [domain, source] of Object.entries(REFERER_MAP)) {
    if (matchesDomain(host, domain)) return source;
  }
  // An outside site nobody mapped yet: keep its name rather than calling it "direct"
  return host;
}

/**
 * Which app's built-in browser the page is open in, from the user agent.
 * Named apps double as a source when nothing else identifies one; the generic
 * webview values only describe the browser.
 * @returns {string|null}
 */
export function detectInAppBrowser(userAgent) {
  const ua = userAgent || '';
  if (/Instagram/i.test(ua)) return 'instagram';
  if (/Barcelona/.test(ua)) return 'threads';
  if (/FBAN\/Messenger|MessengerForiOS|FB_IAB\/Orca|\bOrca-Android/i.test(ua)) return 'messenger';
  if (/FBAN|FBAV|FB_IAB|FBIOS/i.test(ua)) return 'facebook';
  if (/musical_ly|BytedanceWebview|TikTok/i.test(ua)) return 'tiktok';
  if (/\bLine\//i.test(ua)) return 'line';
  if (/Snapchat/i.test(ua)) return 'snapchat';
  if (/LinkedInApp/i.test(ua)) return 'linkedin';
  if (/Telegram/i.test(ua)) return 'telegram';
  if (/; wv\)/.test(ua)) return 'android_webview';
  if (/iPhone|iPad|iPod/.test(ua) && /AppleWebKit/.test(ua) && !/Safari\//.test(ua)) {
    return 'ios_webview';
  }
  return null;
}

const GENERIC_WEBVIEWS = ['android_webview', 'ios_webview'];

const ATTRIBUTION_PARAMS = ['utm_source', ...PLATFORM_ID_MAP.map(([param]) => param)];

/**
 * Detect source from current URL params, referer and user agent.
 *
 * `campaign`/`content` only ever come from UTM params - a referer or a click
 * ID says which platform sent the visitor, never which post did. Tag every
 * link you publish (e.g. an Instagram story) with utm_campaign so the visits
 * it brings can be told apart from the rest of that platform's traffic.
 *
 * @returns {{ source: string, medium: string|null, campaign: string|null, content: string|null }}
 */
export function detectSource() {
  let params;
  try {
    params = new URLSearchParams(window.location.search);
  } catch (error) {
    params = new URLSearchParams();
  }

  // Priority 1: UTM params
  const utmSource = params.get('utm_source');
  if (utmSource) {
    return {
      source: utmSource.trim().toLowerCase(),
      medium: params.get('utm_medium') || null,
      campaign: params.get('utm_campaign') || null,
      content: params.get('utm_content') || null,
    };
  }

  // Priority 2: Platform click IDs
  for (const [paramKey, sourceName, medium] of PLATFORM_ID_MAP) {
    if (params.has(paramKey)) {
      return { source: sourceName, medium, campaign: null, content: null };
    }
  }

  // Priority 3: HTTP Referer
  const fromReferrer = sourceFromReferrer(typeof document !== 'undefined' ? document.referrer : '');
  if (fromReferrer) {
    return { source: fromReferrer, medium: 'referral', campaign: null, content: null };
  }

  // Priority 4: the app this page is open inside
  const inApp = detectInAppBrowser(typeof navigator !== 'undefined' ? navigator.userAgent : '');
  if (inApp && !GENERIC_WEBVIEWS.includes(inApp)) {
    return { source: inApp, medium: 'inapp', campaign: null, content: null };
  }
  let insideToki = false;
  try {
    insideToki = isTokiWebView();
  } catch (error) {
    // not inside Toki
  }
  if (insideToki) {
    return { source: 'toki', medium: 'app', campaign: null, content: null };
  }

  // Priority 5: Default
  return { source: 'direct', medium: null, campaign: null, content: null };
}

/**
 * Whether this page load is a fresh arrival from somewhere — a link with attribution
 * params or from an outside site — rather than a reload, a back/forward step, or a
 * typed URL. Only a fresh arrival may replace the source a tab already has.
 */
function isFreshArrival() {
  try {
    const nav = performance.getEntriesByType?.('navigation')?.[0];
    if (nav && nav.type !== 'navigate') return false;
    const params = new URLSearchParams(window.location.search);
    if (ATTRIBUTION_PARAMS.some((param) => params.has(param))) return true;
    return !!sourceFromReferrer(document.referrer);
  } catch (error) {
    return false;
  }
}

/**
 * Initialize source tracking on app load.
 *
 * The first landing in a tab sets the source. A later full page load in the same tab
 * keeps it — unless it is a fresh arrival from a different channel (say the tab came back
 * to Diil from a Google result an hour later), which starts a new session with the new
 * source, the way GA4 does. Without this the second visit was credited to the first
 * visit's channel and merged into its session.
 */
export function initSourceTracking() {
  try {
    const existing = sessionGetJson(SESSION_KEY, null);
    if (!existing) {
      sessionSetJson(SESSION_KEY, detectSource());
      return;
    }
    if (!isFreshArrival()) return;
    const next = detectSource();
    const same =
      existing.source === next.source &&
      (existing.campaign || null) === (next.campaign || null);
    if (same) return;
    sessionSetJson(SESSION_KEY, next);
    resetViewSession();
  } catch (error) {
    // never let attribution break app start-up
  }
}

/**
 * Get the tracked source.
 * @returns {{ source: string, medium: string|null, campaign: string|null, content: string|null } | null}
 */
export function getTrackedSource() {
  return sessionGetJson(SESSION_KEY, null);
}
